import './PlayerConfig.scss'
import Copyable from '../chrome/Copyable'
import InfoBubble from '../chrome/InfoBubble'
import LabelValue from '../chrome/LabelValue'
import PlayerHelper from '../../players/PlayerHelper'
import React, {ReactElement, useEffect, useState} from 'react'
import useXhr from '../http/useXhr'
import {Accordion, AccordionTab} from 'primereact/accordion'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Stack, Switch, Typography} from '@mui/material'
import {_httpInstance} from '../../services/game-managers/HttpGameManager'

const playerReady = <InfoBubble
    text='Player is ready' icon='pi-check-circle' position='left' />;
const playerWaiting = <InfoBubble
    text='No player here! Take a seat or invite a friend.'
    icon='pi-exclamation-triangle'
    position='left' />;

const defaultNewPlayer = {
    'playerType': 'human', 'owner': null, 'name': 'Player 1', 'style': 'A'};
const defaultPlayerName = i => `Player ${i + 1}`;
   

/**
 * props:
 *   gameType
 *   players
 *   allowedPlayerCounts
 *   onCommit(players)
 *   readOnly
 *   playMode
 *   onPlayModeChange(m)
 *   gameManager
 */
const PlayerConfig = props => {
  const isLocal = !props.playMode || props.playMode === 'local'
  const gameManager = props.gameManager

  const [editPlayerNum, setEditPlayerNum] = useState<number>();
  const [editedName, setEditedName] = useState<string>();
  const [editedOwner, setEditedOwner] = useState();

  const editPlayer = editPlayerNum !== undefined ?
      props.players[editPlayerNum] : undefined;
  const botList = useXhr(
      [], '/gameplay/settings/botlist', {gameType: props.gameType});
  let avatarPanelRef: OverlayPanel | null = null;

  useEffect(() => { // Debounce name typing.
    let timerId: NodeJS.Timeout | undefined;
    if (editedName && editPlayerNum != null) {
      timerId = setTimeout(() => onCommitName(editedName), 500);
    }
    return () => clearTimeout(timerId);
  });

  useEffect(() => {
    if (props.readOnly) {
      setEditPlayerNum(undefined);
    }
  }, [props.readOnly]);

  const onSwap = (index1, index2) => {
    let tempPlayer = props.players[index1];
    props.players[index1] = props.players[index2];
    props.players[index2] = tempPlayer;
    setEditPlayerNum(undefined);
    props.onCommit && props.onCommit(props.players);
  };

  const onSetPlayerCount = n => {
    let newPlayers = props.players;
    let usedStyles = props.players.map(p => p.style);
    let availableStyles =
        PlayerHelper.getAllStyles().filter(s => usedStyles.indexOf(s) === -1);
    if (n < props.players.length) {
      newPlayers = props.players.slice(0, n);
    }
    for (let i = props.players.length; i < n; i++) {
      let style = availableStyles.shift() || defaultNewPlayer.style;
      let name = defaultPlayerName(i);
      let player = {...defaultNewPlayer, name, style};
      PlayerHelper.claimPlayer(player);
      newPlayers.push(player);
    }
    setEditPlayerNum(undefined);
    props.onCommit && props.onCommit(newPlayers);
  };

  const onCommitBotType = botType => {
    let botData = botList.find(el => el.owner === botType);
    if (editPlayerNum === undefined) return

    let player = props.players[editPlayerNum];
    PlayerHelper.setAsBot(player, botData);
    setEditedName(player.name);
    setEditedOwner(player.owner);
    props.onCommit && props.onCommit(props.players);
  };

  const onCommitStyle = style => {
    if (editPlayerNum === undefined) return
    props.players[editPlayerNum].style = style;
    props.onCommit && props.onCommit(props.players);
  };

  const onCommitName = name => {
    if (editPlayerNum === undefined) return
    let player = props.players[editPlayerNum];
    if (player.name !== name) {
      props.players[editPlayerNum].name = name;
      props.onCommit && props.onCommit(props.players);
    }
  };


  // TODO: Maybe not needed
  const onCommitToggleType = (player: any, index: number) => {
    // if (editPlayerNum === undefined) return
    // Three-way toggle path: This Device, Other Device, Bot

    let isBotAllowed = botList.length > 0;
    // let player = props.players[editPlayerNum];

    if (PlayerHelper.isOwnedByMe(player)) {
      PlayerHelper.unclaimPlayer(player);
    } else if (PlayerHelper.isUnowned(player) && !isBotAllowed) {
      PlayerHelper.claimPlayer(player);
      player.name = defaultPlayerName(index);
    } else if (PlayerHelper.isUnowned(player) && isBotAllowed) {
      PlayerHelper.setAsBot(player, botList[0]);
    } else if (PlayerHelper.isBot(player)) {
      PlayerHelper.claimPlayer(player);
      player.name = defaultPlayerName(index);
    }
    setEditedName(player.name);
    setEditedOwner(player.owner);
    props.onCommit && props.onCommit(props.players);
  };

  const avatarFor = player => {
    let img = PlayerHelper.getAvatar(player);
    return { backgroundImage: `url(${img})` };
  };

  const renderAvatarSelector = () => {
    let styles = PlayerHelper.getAllStyles();
    let playerType = editPlayer ? editPlayer.playerType : null;
    return (
      <OverlayPanel ref={el => avatarPanelRef = el}>
        {styles.map(style => <div
            className='avatar avatarChoice' key={style}
            style={avatarFor({style, playerType})}
            onClick={() => onCommitStyle(style)} /> )}

      </OverlayPanel>
    );
  };

  const renderName = (player, isEditable) => {
    let content = <div>{player.name}</div>;
    if (isEditable && PlayerHelper.isBot(player)) {
      content = <Dropdown
          optionLabel='name'
          optionValue='owner'
          value={editedOwner}
          options={botList}
          onChange={e => onCommitBotType(e.value)} />;
    } else if (isEditable) {
      content = <InputText
          value={editedName} onChange={e => setEditedName(e.target.value)} />;
    }
    return (
      <div className={`playerName ${PlayerHelper.getStyleClass(player)}`}>
        {content}
      </div>
    );
  };

  function renderEditablePlayer(player, index) {
    return (
      <div
        className="avatar" style={avatarFor(player)}
        onClick={e => avatarPanelRef?.toggle(e)}/>
    )
  }

  const renderPlayer = (player, index) => {
    let editable = editPlayerNum === index;
    let avatarJsx = <div className='avatar' style={avatarFor(player)} />;
    let nameJsx = renderName(player, editable);
    let typeJsx = (isLocal ? null :
        <div className="type">
          <Button
            label={PlayerHelper.getType(player)} className="p-button-outlined"
            onClick={() => _httpInstance.sit(index)}/>
        </div>
    )
    // let typeJsx = isLocal ? null : <div className='type'>{PlayerHelper.getType(player)}</div>;
    let editPlayerBubbleText = 'Edit name, avatar, or player type';
    if (editable) {
      avatarJsx = renderEditablePlayer(player, index);
    }
    if (props.readOnly) {
      editPlayerBubbleText = 'End the current game to edit players';
    }

    return (
      <div
          className={`
              player ${editable ? 'editable' : ''}
              ${props.players.length >= 3 ? 'threeOrMorePlayers' : null}
          `}
          key={index}>
        <div className='playerRow'>
          <InfoBubble
              className='moveUp' icon='pi-angle-up' text='Move player up'
              onClick={() => onSwap(index - 1, index)} />
          <InfoBubble
              className='moveDown' icon='pi-angle-down' text='Move player down'
              onClick={() => onSwap(index, index + 1)} />
          <InfoBubble
              text={editPlayerBubbleText} icon='pi-user-edit'
              className='edit'
              onClick={() => {
                if (props.readOnly) {
                  return;
                }
                let val = editable ? null : index;
                setEditPlayerNum(val);
                setEditedName(val !== null ? props.players[val].name : null);
                setEditedOwner(val !== null ? props.players[val].owner : null);
              }} />
          {avatarJsx}
          <div className='spacer'>
            {editable ?  <div className='desktop'>{nameJsx}</div> : nameJsx}
          </div>
          {typeJsx}
          {isLocal ? null : (
            player.owner ? playerReady : playerWaiting
          )}
        </div>
        {editable ? <div className='mobile'>
          <div className='playerRow'>
            {nameJsx}
          </div>
        </div> : null}
      </div>
    );
  };

  const renderReadOnlyMessage = () => {
    return (
      <div className='readOnlyMessage'>
        You may modify settings between games.
      </div>
    );
  };

  const renderShareLink = () => {
    return (
      <div className='shareLink'>
        <Copyable label='copy game link' value={gameManager.getJoinLink()} />
      </div>
    );
  };

  const renderSetPlayerCountButton = n => {
    return (
      <div
          className={`
              setPlayerCountButton
              ${props.readOnly ? 'readOnly' : 'clickable'}
              ${n === props.players.length ? 'selected' : null}`}
          key={n} onClick={() => { if (!props.readOnly) onSetPlayerCount(n); }}>
        {n}
      </div>
    );
  };

  const renderPlayerCount = () => {
    if (!props.allowedPlayerCounts) {
      return null;
    }
    let buttons = (
      <div className='playerCountButtons'>
        {props.allowedPlayerCounts.map(renderSetPlayerCountButton)}
      </div>
    );
    return (
      <LabelValue
          className='playerCountSelector'
          label='Player count' value={buttons}
          styles={LabelValue.Style.LEFT_RIGHT} />
    );
  };

  const renderMoreInfo = () => (
    <Accordion className='moreInfo'>
      <AccordionTab header={
        <div>
          <i className="pi pi-question-circle" />
          How to play multiplayer
        </div>
      }>
        <p>It’s easy to play with a distant friend! Just follow these steps.</p>
        <ol>
          {props.readOnly ?
              <li>
                Select <em>Quit Game</em> to end the current game, and return
                to this window.
              </li> : null}
          <li>Tap the <em>Edit Player</em> button.</li>
          <li>Tap <em>This Device</em> to relinquish a seat.</li>
          <li>Copy the <em>Game Link</em> and share with a friend.</li>
          <li>When your friend joins, they’ll join in the open slot.</li>
        </ol>
      </AccordionTab>
    </Accordion>
  );

  const renderPlayModeToggle = () => {
    return <div>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography>Local</Typography>
        <Switch checked={!isLocal} onChange={event => {
          props.onPlayModeChange(event.target.checked ? 'remote' : 'local')
        }} />
        <Typography>Party!</Typography>
      </Stack>
    </div>
  }

  return (
    <div className={`
          PlayerConfig ${props.className} ${props.readOnly ? 'readOnly' : ''}`}>
      {renderReadOnlyMessage()}
      {renderAvatarSelector()}
      {renderPlayerCount()}
      <div className='players'>
        {props.players.map(renderPlayer)}
      </div>
      {renderPlayModeToggle()}

      { !isLocal && renderShareLink()}
      {/*{renderMoreInfo()}*/}
    </div>
  );
};


export default PlayerConfig;
